import { graphql, type HeadFC } from 'gatsby';
import React, { useEffect, useState } from 'react';

import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import 'src/styling/global.scss';

import ErrorMessage from 'src/components/common/error-message';
import type { PageContext } from 'src/components/common/page-context';
import { PageHead } from 'src/components/common/page-head';
import SuccessfulVerification from 'src/components/common/successful-verification';
import { useQueryStringValue } from 'src/custom-hooks/use-query-string-value';

import Spinner from '../components/common/spinner';
import { put } from '../helpers/request-helper';

import * as styles from './contact-verification.module.scss';

enum PAGE_STATE {
  LOADING,
  LOADED,
  ERROR,
}

const verifyMembershipRegistration = (verificationCode: string) =>
  put('membership/verify', { verificationCode });

export const MembershipAndContactVerificationLanding: React.FC = () => {
  const verificationCode = useQueryStringValue('verificationCode');

  const [verificationResult, setVerificationResult] = useState<'pending' | 'loaded' | 'error'>(
    'pending'
  );
  const [retryCounter, setRetryCounter] = useState(0);

  useEffect(() => {
    if (!verificationCode) {
      return;
    }
    let cancelled = false;
    verifyMembershipRegistration(verificationCode)
      .then(() => {
        if (!cancelled) setVerificationResult('loaded');
      })
      .catch(() => {
        if (!cancelled) setVerificationResult('error');
      });
    return () => {
      cancelled = true;
    };
  }, [verificationCode, retryCounter]);

  const onRetryClicked = () => {
    setVerificationResult('pending');
    setRetryCounter((n) => n + 1);
  };

  const pageState: PAGE_STATE = !verificationCode
    ? PAGE_STATE.ERROR
    : verificationResult === 'loaded'
      ? PAGE_STATE.LOADED
      : verificationResult === 'error'
        ? PAGE_STATE.ERROR
        : PAGE_STATE.LOADING;

  switch (pageState) {
    case PAGE_STATE.ERROR:
      return <ErrorMessage onRetryClicked={onRetryClicked} />;
    case PAGE_STATE.LOADED:
      return (
        <SuccessfulVerification
          titleMessageKey="thankYouForSubscribing"
          subtitleMessageKey="downloadTheMobileAppHere"
        />
      );
    case PAGE_STATE.LOADING:
      return (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      );
  }
};

const MembershipAndContactVerification: React.FC = () => (
  <MembershipAndContactVerificationLanding />
);

export default MembershipAndContactVerification;

export const Head: HeadFC<never, PageContext> = ({ pageContext }) => {
  return (
    <PageHead
      pageContext={pageContext}
      seo={{ titleOverride: 'Membership and Contact Verification' }}
    />
  );
};

export const query = graphql`
  query MembershipAndContactVerificationPage($globalDirectoryName: String!) {
    ..._GlobalCmsRecordCore
  }
`;
